<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { ModalDialog, LoadingView, arrayHelpers, AllowedFontSize, formatHelpers, ToastTypes, PrimaryButton } from '@hfc/ui-core';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import PlanListItem from '@/planList/infrastructure/components/PlanListItem.vue';
import SmallBusinessFilters from '@/application/smallBusiness/infrastructure/components/SmallBusinessFilters.vue';
import { SmallBusinessPlan } from '@/application/smallBusiness/domain/SmallBusinessPlan';
import ComparePlans from '@/application/shared/infrastructure/components/ComparePlans.vue';
import { useComparePlansStore } from '@/shared/infrastructure/composition/useComparePlansStore';
import PricingAndBenefits from '@/application/smallBusiness/infrastructure/components/PricingAndBenefits.vue';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';

interface EmitInterface {
  (event: 'nextSection'): void;
}

const emits = defineEmits<EmitInterface>();

const { appStore } = useAppStore();

const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();
const { comparePlansStore, comparePlansState } = useComparePlansStore();

const { groupBy } = arrayHelpers();
const { formatPrice } = formatHelpers();

const applicationType = APPLICATION_TYPES.SMALL_BUSINESS;

const modalDetailsPlan = ref<SmallBusinessPlan | null>(null);

const availablePlans = computed(() => smallBusinessState.value.filteredPlans as Array<SmallBusinessPlan>);
const groupedPlansByTier = computed(() => sortAndOrderPlans(groupBy<SmallBusinessPlan, 'metal_tier'>(availablePlans.value, 'metal_tier')));

const tierOrder = ['bronze', 'silver', 'gold', 'platinum'];

const sortAndOrderPlans = (grouped: Map<string, Array<SmallBusinessPlan>>): Map<string, Array<SmallBusinessPlan>> => {
  return new Map(
    Array.from(grouped.entries())
      .sort(([a], [b]) => tierOrder.indexOf(a.toLowerCase()) - tierOrder.indexOf(b.toLowerCase()))
      .map(([tier, plans]) => [
        tier,
        [...plans].sort((a, b) => a.rate - b.rate)
      ])
  );
};

const setSelectedPlan = async (plan: SmallBusinessPlan): Promise<void> => {
  await smallBusinessStore.removeAllSelectedPlans();
  await smallBusinessStore.addSelectedPlan(plan);

  emits('nextSection');
};

const checkSelectedPlan = (plan: SmallBusinessPlan): boolean => {
  return !!smallBusinessState.value.application.plans?.find(selectedPlan => selectedPlan.reference_id === plan.reference_id);
};

onMounted(async () => {
  await smallBusinessStore.enableUpdateAvailableFilters();
});

const onSubmit = (): boolean => {
  if (smallBusinessState.value.application.plans?.length !== 0) {
    return true;
  }
  appStore.addToast({
    type: ToastTypes.WARNING,
    message: 'Please select a plan to continue.',
  });

  return false;
};

defineExpose({
  sectionSubmit: onSubmit,
});
</script>
<template>
  <BaseSection class="items-center text-center" :class="comparePlansState.plansToCompare.length > 0 ? 'mb-20' : ''">
    <SmallBusinessFilters></SmallBusinessFilters>
    <div class="flex w-full items-center flex-col md:flex-row gap-4 md:gap-0 pb-4">
      <div v-if="smallBusinessState.availablePlansLoading" class="w-full h-full flex justify-center items-center min-h-[400px]">
        <LoadingView class="bg-white"/>
      </div>
      <div v-else class="flex w-full flex-col space-y-6">
        <div v-if="groupedPlansByTier.size === 0" class="flex flex-col">
          <p>No plans available.</p>
        </div>
        <div v-else v-for="[tierName, tierPlans] in groupedPlansByTier" :key="tierName" class="flex w-full flex-col">
          <div class="flex flex-row items-center mb-4">
            <h2 class="text-2xl font-bold capitalize">{{ tierName }} Plans</h2><span class="text-blue-600 ml-2">({{ tierPlans.length }} option{{ tierPlans.length > 1 ? 's' : '' }})</span>
          </div>
          <div class="flex flex-row bg-gray-200 rounded-xl py-4 overflow-x-auto">
            <PlanListItem
              v-for="(plan, i) in tierPlans"
              :plan="plan"
              :cols="4"
              :compact="true"
              :key="i"
              :view-details-button="true"
              :view-comparison-check="true"
              :application-type="applicationType"
              :is-selected="checkSelectedPlan(plan)"
              :hide-price-title="true"
              price-title="You pay per employee"
              price-attribute="rate"
              :price-title-size="AllowedFontSize.XXL"
              view-details-text="View Details"
              @setDetailsModal="modalDetailsPlan = plan"
              @selectPlan="setSelectedPlan(plan)"
              @unSelectPlan="smallBusinessStore.removeSelectedPlan(plan)"
              @set-plan-to-compare="comparePlansStore.addPlanToCompare(plan)">
              <template #plan-name>
                <span class="lining-nums" v-html="plan.twoLineName"></span>
              </template>
              <template #under-price>
                <div class="flex flex-col items-center lining-nums mx-10">
                  <div>Business pays: <span class="font-bold">{{ formatPrice(plan.totalEmployerContribution) }}</span></div>
                  <div>Employees pay: <span class="font-bold">{{ formatPrice(plan.totalEmployeeCost) }}</span></div>
                </div>
              </template>
            </PlanListItem>
          </div>
        </div>
      </div>
    </div>
  </BaseSection>
  <ComparePlans price-attribute="average_per_employee" price-label="employee per month" />
  <ModalDialog v-if="modalDetailsPlan" @close="modalDetailsPlan = null" confirm-button="Select plan" :confirm-button-type="PrimaryButton" size="xl" @confirm="setSelectedPlan(modalDetailsPlan)">
    <template #content>
      <PricingAndBenefits :plan="modalDetailsPlan" :application-type="applicationType" :dependent-contribution="smallBusinessState.application.contribution.dependent_amount" :employee-contribution="smallBusinessState.application.contribution.employee_amount" :contribution-type="smallBusinessState.application.contribution.type" />
    </template>
  </ModalDialog>
</template>
